import React, { useEffect, useState } from 'react';
import {
    Chip as ChipBase,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TablePagination,
    TableRow,
    TextField,
    Toolbar,
    Tooltip,
} from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Search as SearchIcon, Warning as WarningIcon, FileCopyOutlined } from '@material-ui/icons';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

// styles
import useStyles from './styles';

// components
import { PlayCircleOutline as PlayIcon } from '@material-ui/icons';
import PropTypes from 'prop-types';
import Widget from '../../components/Widget';
import EnhancedTableHead from '../../components/Table/EnhancedTableHead';
import EmptyTableRow from '../../components/Table/EmptyTableRow';
import { Chip, Typography } from '../../components/Wrappers';

// context
import { useOrderDispatch, useOrderState, actions as orderActions } from '../../context/OrderContext';
import { usePracticeDispatch, usePracticeState, actions as practiceActions } from '../../context/PracticeContext';

// helpers
import { stableSort, getSorting, getPracticeTradingName } from '../../common/helpers';
import SortTableSelect from '../../components/Table/SortTableSelect';
import { toast } from 'react-toastify';
import { colorForStatus, orderStatuses } from '../new-order/helpers/statuses';

const headCells = [
    { id: 'copy', alignLeft: true, disablePadding: false, label: '', isSortable: false },
    { id: 'tradingName', alignLeft: true, disablePadding: false, label: 'Clinic Name', isSortable: false },
    { id: 'territory', alignLeft: true, disablePadding: false, label: 'Area', isSortable: false },
    { id: 'prescriberName', alignLeft: true, disablePadding: false, label: 'Doctor', isSortable: true },
    { id: 'patientName', alignLeft: true, disablePadding: false, label: 'Patient', isSortable: true },
    { id: 'caseNumber', alignLeft: true, disablePadding: false, label: 'Case#', isSortable: false },
    { id: 'createdAt', alignLeft: true, disablePadding: false, label: 'Date', isSortable: true },
    { id: 'status', alignLeft: true, disablePadding: false, label: 'Status', isSortable: false },
    { id: 'refinement', alignLeft: true, disablePadding: false, label: 'REF', isSortable: false },
    { id: 'treatmentType', alignLeft: true, disablePadding: false, label: 'TX', isSortable: false },
    { id: 'orderType', alignLeft: true, disablePadding: false, label: 'Order Type', isSortable: false },
    { id: 'finalize', alignLeft: true, disablePadding: false, label: '', isSortable: false },
];

/**
 * Collect the N to N details from the treatment type
 * @param {*} treatmentType: Treatment From Canine to Canine (3 to 3)
 * @returns 3-3
 */
const getShortTreatmentType = (treatmentType) => {
    const matchResult = treatmentType.match(/\d to \d/);
    if (!matchResult) return '';
    return matchResult[0].replace(' to ', '-');
};

/**
 * Returns orders matching the search input
 */
const filterItems = (orders, practices, input) => {
    var regexp = new RegExp(input, 'gi');
    return orders.filter((o) => {
        const treatmentType = getShortTreatmentType(o.treatmentType);
        const practiceTradingName = getPracticeTradingName(practices, o.practiceId);
        // keep the case number and serial number in the search criteria even if they are not displayed
        return (
            (treatmentType && treatmentType.match(regexp)) ||
            practiceTradingName?.match(regexp) ||
            o.patientName?.match(regexp) ||
            o.prescriberName?.match(regexp) ||
            o.caseNumber?.match(regexp) ||
            o.caseId?.match(regexp) ||
            o.serialNumber?.match(regexp) ||
            o.orderStatus?.match(regexp) ||
            o.territory?.match(regexp) ||
            o.orderType?.match(regexp)
        );
    });
};

const OrderTableRow = ({ classes, headers, row, practices }) => {
    const history = useHistory();

    const mustFinalizeOrder =
        row.orderType === 'nova' &&
        (row.orderStatus === orderStatuses.WAITING_FOR_IMPRESSION ||
            row.orderStatus === orderStatuses.WAITING_FOR_TRANSFER);

    const handleCopyToClipboard = (event) => {
        // Stop event propagation to prevent TableRow onClick from firing
        event.stopPropagation();

        const textToCopy = `Patient name: ${row.patientName} / Case #: ${row.caseNumber || row.caseId} / SN: ${
            row.serialNumber
        }`;
        navigator.clipboard.writeText(textToCopy);
        toast.success('Order details copied to clipboard');
    };

    const handleViewOrder = () => history.push(`view?practiceId=${row.practiceId}&orderId=${row.orderId}`);

    const handleFinalizeOrder = (event) => {
        // Stop event propagation to prevent TableRow onClick from firing
        event.stopPropagation();

        if (row.orderType === 'nova' && row.refinement === 'Yes') {
            history.push(`/app/nova-order/refine?practiceId=${row.practiceId}&orderId=${row.orderId}&finalize=true`);
        } else if (row.orderType === 'nova') {
            history.push(`/app/nova-order/add?practiceId=${row.practiceId}&orderId=${row.orderId}`);
        } else {
            history.push(`add?practiceId=${row.practiceId}&orderId=${row.orderId}`);
        }
    };

    return (
        <TableRow hover tabIndex={-1} key={row.orderId} onClick={handleViewOrder}>
            <TableCell
                label={headers[0].label}
                align="left"
                style={{ whiteSpace: 'nowrap', paddingLeft: 1, paddingRight: 1 }}
            >
                <IconButton
                    className={classes.actionsIcon}
                    aria-owns="actions-menu"
                    aria-haspopup="true"
                    onClick={handleCopyToClipboard}
                >
                    <FileCopyOutlined color="primary" />
                </IconButton>
            </TableCell>

            <TableCell label={headers[1].label}>{getPracticeTradingName(practices, row.practiceId)}</TableCell>
            <TableCell label={headers[2].label}>{row.territory}</TableCell>
            <TableCell label={headers[3].label}>{row.prescriberName}</TableCell>
            <TableCell label={headers[4].label}>{row.patientName}</TableCell>
            <TableCell label={headers[5].label}>{row.caseNumber || row.caseId || row.serialNumber}</TableCell>
            <TableCell label={headers[6].label}>
                {row.createdAt ? moment(row.createdAt).format('DD/MM/YY') : ''}
            </TableCell>
            <TableCell label={headers[7].label} style={{ whiteSpace: 'nowrap' }}>
                <Chip
                    label={row.orderStatus.replace('Case', '') || 'Unknown'}
                    color={colorForStatus(row.orderStatus)}
                />
                {row.orderStatus === orderStatuses.CASE_NEEDS_APPROVAL && row.treatmentInvoiced && (
                    <Tooltip
                        title={
                            <Typography fontSize={30}>
                                Case pending for over 2 months. Treatment plan only has been invoiced and will be
                                deducted from total course cost when case is approved.
                            </Typography>
                        }
                    >
                        <IconButton>
                            <WarningIcon color="primary" />
                        </IconButton>
                    </Tooltip>
                )}
            </TableCell>
            <TableCell label={headers[8].label}>{row.refinement === 'Yes' ? 'Yes' : ''}</TableCell>
            <TableCell label={headers[9].label} style={{ whiteSpace: 'nowrap' }}>
                {getShortTreatmentType(row.treatmentType)}
            </TableCell>
            <TableCell label={headers[10].label} style={{ whiteSpace: 'nowrap' }}>
                {row.orderType || '3Shape'}
            </TableCell>
            <TableCell
                label={headers[11].label}
                align="left"
                style={{ whiteSpace: 'nowrap', paddingLeft: 1, paddingRight: 1 }}
            >
                {mustFinalizeOrder && (
                    <Tooltip
                        title={
                            <Typography fontSize={30}>
                                {`Finalise ${
                                    row.refinement === 'Yes' ? 'refinement' : 'order'
                                } when impression or transfer is received`}
                            </Typography>
                        }
                    >
                        <IconButton
                            className={classes.actionsIcon}
                            aria-owns="actions-menu"
                            aria-haspopup="true"
                            onClick={handleFinalizeOrder}
                        >
                            <PlayIcon color="primary" />
                        </IconButton>
                    </Tooltip>
                )}
            </TableCell>
        </TableRow>
    );
};

OrderTableRow.propTypes = {
    classes: PropTypes.object.isRequired,
    headers: PropTypes.array.isRequired,
    row: PropTypes.object.isRequired,
    practices: PropTypes.array.isRequired,
};

const EnhancedTableToolbar = ({
    classes,
    onSearchInputChange,
    onOrderStatusChange,
    onRequestSort,
    searchValue,
    headers,
    order,
    orderBy,
}) => {
    const displaySortTableSelect = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    return (
        <Toolbar style={{ marginTop: 10, marginBottom: 10 }}>
            <Grid container justifyContent="flex-start" spacing={2}>
                <Grid item xs={12} md={6} lg={4} xl={2} style={{ display: 'flex', gap: 8 }}>
                    <ChipBase
                        label="Cases in progress"
                        onClick={onOrderStatusChange('in-progress')}
                        className={classNames(classes.orderStatusChip, {
                            active: searchValue.orderStatus === 'in-progress',
                        })}
                    />
                    <ChipBase
                        label="Cases completed"
                        onClick={onOrderStatusChange('complete')}
                        className={classNames(classes.orderStatusChip, {
                            active: searchValue.orderStatus === 'complete',
                        })}
                    />
                </Grid>
                <Grid item xs={12} md={4} style={{ display: 'flex' }}>
                    <IconButton>
                        <SearchIcon />
                    </IconButton>
                    <TextField
                        color="secondary"
                        type="search"
                        placeholder="Search"
                        fullWidth
                        onChange={onSearchInputChange}
                        value={searchValue.searchInput || ''}
                    />
                </Grid>
                {displaySortTableSelect && (
                    <Grid item xs={12} md={4} style={{ display: 'flex', marginBottom: 10 }}>
                        <SortTableSelect
                            headers={headers}
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={onRequestSort}
                        ></SortTableSelect>
                    </Grid>
                )}
            </Grid>
        </Toolbar>
    );
};

EnhancedTableToolbar.propTypes = {
    classes: PropTypes.object.isRequired,
    onSearchInputChange: PropTypes.func.isRequired,
    onOrderStatusChange: PropTypes.func.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    searchValue: PropTypes.object.isRequired,
    headers: PropTypes.array.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
};

const OrderList = () => {
    const classes = useStyles();
    const orderDispatch = useOrderDispatch();
    const orderState = useOrderState();
    const practiceDispatch = usePracticeDispatch();
    const practiceState = usePracticeState();
    const history = useHistory();
    const location = useLocation();

    // order and page preferences
    const { order, orderBy, rowsPerPage, page } = orderState.listPreferences;

    // Search feature
    const [filteredItems, setFilteredItems] = useState([]);

    // =================== Hooks ======================

    useEffect(() => {
        if (!practiceState.practices) {
            practiceActions.doFetch()(practiceDispatch);
        }
    }, []); // eslint-disable-line

    /**
     * Check the query string to set the order status filter
     */
    useEffect(() => {
        const queryString = new URLSearchParams(location.search);
        const orderStatus = queryString.get('orderStatus');
        const currentStatusFilter = orderState.search.orderStatus;
        if (orderStatus?.match('in-progress|complete') && orderStatus !== currentStatusFilter) {
            // the orderStatus filter must be updated
            orderActions.doResetState()(orderDispatch);
            const search = { searchInput: '', orderStatus };
            orderActions.doSearchOrders(search)(orderDispatch);
        }
    }, [location.search]); //eslint-disable-line

    useEffect(() => {
        setFilteredItems(orderState.orders?.items || []);
    }, [orderState.orders]); // eslint-disable-line

    /**
     * Fetch orders when order status changes
     * The state is reset on status change so only fetch when no order is found
     */
    useEffect(() => {
        if (orderState.search.orderStatus && !orderState.orders) {
            orderActions.doFetch({ orderStatus: orderState.search.orderStatus })(orderDispatch);
        }
    }, [orderState.search.orderStatus]); // eslint-disable-line

    useEffect(() => {
        const input = orderState.search.searchInput || '';
        const orders = orderState.orders?.items || [];
        if (input.length === 0) {
            setFilteredItems(orders);
        } else if (input.length > 1) {
            const practices = practiceState.practices?.items || [];
            setFilteredItems(filterItems(orders, practices, input));
        }
    }, [orderState.search.searchInput]); // eslint-disable-line

    // =================== Handlers ======================

    const handleRequestSort = (_, property) => {
        const isDesc = orderBy === property && order === 'desc';
        const preferences = { ...orderState.listPreferences, order: isDesc ? 'asc' : 'desc', orderBy: property };
        orderActions.doChangeListPreferences(preferences)(orderDispatch);
    };

    const handleChangePage = (_, newPage) => {
        const preferences = { ...orderState.listPreferences, page: newPage };
        orderActions.doChangeListPreferences(preferences)(orderDispatch);
    };

    const handleChangeRowsPerPage = (event) => {
        const preferences = { ...orderState.listPreferences, rowsPerPage: parseInt(event.target.value, 10), page: 0 };
        orderActions.doChangeListPreferences(preferences)(orderDispatch);
    };

    const handleSearchInputChange = (e) => {
        const search = { ...orderState.search, searchInput: e.target.value };
        orderActions.doSearchOrders(search)(orderDispatch);
    };

    const handleOrderStatusFilter = (newStatus) => () => {
        orderActions.doResetState()(orderDispatch);
        const search = { searchInput: '', orderStatus: newStatus };
        orderActions.doSearchOrders(search)(orderDispatch);
        // update the query string in the url according to the orderStatus filter value
        history.replace(`list?orderStatus=${newStatus}`);
    };

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Widget noBodyPadding bodyClass={classes.tableWidget}>
                    <EnhancedTableToolbar
                        classes={classes}
                        onSearchInputChange={handleSearchInputChange}
                        onOrderStatusChange={handleOrderStatusFilter}
                        onRequestSort={handleRequestSort}
                        searchValue={orderState.search}
                        headers={headCells}
                        order={order}
                        orderBy={orderBy}
                    />

                    <div className={classes.tableWrapper}>
                        <Table className={classes.table} aria-labelledby="tableTitle" aria-label="recent orders">
                            <EnhancedTableHead
                                classes={classes}
                                headers={headCells}
                                order={order}
                                orderBy={orderBy}
                                onRequestSort={handleRequestSort}
                                rowCount={filteredItems.length}
                            />

                            <TableBody>
                                {(orderState.fetchLoading || filteredItems?.length === 0) && (
                                    <EmptyTableRow
                                        nbColumns={headCells.length}
                                        isLoading={orderState.fetchLoading}
                                        label="No order found"
                                    />
                                )}

                                {!orderState.fetchLoading &&
                                    filteredItems?.length > 0 &&
                                    stableSort(filteredItems, getSorting(order, orderBy))
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row) => (
                                            <OrderTableRow
                                                key={row.orderId}
                                                classes={classes}
                                                headers={headCells}
                                                row={row}
                                                practices={practiceState.practices?.items || []}
                                            />
                                        ))}
                            </TableBody>
                        </Table>
                    </div>

                    <TablePagination
                        rowsPerPageOptions={[50, 100]}
                        component="div"
                        count={filteredItems.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        backIconButtonProps={{ 'aria-label': 'previous page' }}
                        nextIconButtonProps={{ 'aria-label': 'next page' }}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Widget>
            </Grid>
        </Grid>
    );
};

export default OrderList;
